// Shared module-level state and utilities for the UOM-in-pill feature.
// Both SelectionOption (writes) and UnitsOfMeasure (reads) import from here
// instead of cross-importing each other.
import type { ChildTextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { TrackChangeType } from 'models/changes'
import type React from 'react'
import { ELEMENT_TYPE } from '../../constants/elementTypes'
import type { ContentItemViewNodesType } from '../../types/contentItemViewNode.type'

export const UOM_MAX_LENGTH = 25
export const UOM_MAX_SPECIAL_CHARS = 10

// pillId (SelectionOption ID) → Map<textElId, currentTypedValue>
// Written per-keystroke in text inputs (no React state → no re-render delay).
// Read by UnitsOfMeasure.saveAll at pill-blur time to include text in the store/API update.
export const pillTextCache = new Map<string, Map<string, string>>()

// Signals handleFocusOut in UnitsOfMeasure to skip saveAll when the user pressed Escape.
// SelectionOption.handlePillEscape adds the pill id; UnitsOfMeasure.handleFocusOut checks/deletes it.
export const cancellingPills = new Set<string>()

// Typed inline text captured before action bar "Insert UOM" click; read by buildUOMHandlerForNewPill.
export const pillInlineTextBeforeUOM = new Map<string, string>()

// Pill IDs mid Ctrl+U insert, so useUOMSave.handleFocusOut skips saveAll during the insert commit.
// Armed in useUOMInlinePillHandlers.handleInsertUOMKeyDown; released event-driven, not on a timer.
export const insertingUOMViaKeyboardPills = new Set<string>()

// Resize an input to fit its rendered text content.
// Collapses to 1px first so scrollWidth reflects true content width, then restores.
// +1 prevents sub-pixel clipping on high-DPI displays (scrollWidth rounds to integers).
export const resizeInput = (input: HTMLInputElement, buffer = 1) => {
    input.style.width = '1px'
    input.style.width = `${input.scrollWidth + buffer}px`
}

// Prevents the pill from stealing focus from the currently-active input when the
// user clicks pill padding. Clicks directly into a UOM box are allowed through.
export const handleUOMPillMouseDown = (e: React.MouseEvent) => {
    const isTargetingUOM = !!(e.target as HTMLElement).closest(
        '[data-suggestion^="units-of-measure-"]'
    )
    if (!isTargetingUOM && (e.currentTarget as HTMLElement).contains(document.activeElement)) {
        e.preventDefault()
    }
}

// Derives the UOM children that should render as inline editors and whether
// an active UOM insert is in progress. Extracted from OptionGroup so that
// the filtering logic lives in the feature folder rather than in the renderer.
export const getUOMPillChildren = (
    elements: Partial<ChildTextContentElement>[] | undefined,
    isOnOMWorkspace: boolean
) => {
    const uomChildren = (elements ?? []).filter((c) => c?.type === ELEMENT_TYPE.UNITS_OF_MEASURE)
    return {
        uomChildrenToRender: isOnOMWorkspace
            ? uomChildren.filter((c) => c?.changeType === TrackChangeType.ADDED)
            : [],
        hasActiveUOMInsert: uomChildren.some((c) => c?.changeType === TrackChangeType.ADDED),
    }
}

// Finds the SelectionOption by ID within the node tree and replaces it with the
// result of updateSO. Short-circuits after the first match.
export const updateSOInNodes = (
    nodes: Partial<ContentItemViewNodesType>[],
    selectionOptionId: string,
    updateSO: (so: Partial<ChildTextContentElement>) => Partial<ChildTextContentElement>
): Partial<ContentItemViewNodesType>[] => {
    let done = false
    return nodes.map((node) => {
        if (done) return node
        const elements = node.elements ?? []
        let soFound = false
        const newElements = elements.map((ogEl) => {
            if (soFound || ogEl.type !== 'OptionGroup') return ogEl
            const soElements = ogEl.elements ?? []
            const soIndex = soElements.findIndex((el) => el.id === selectionOptionId)
            if (soIndex === -1) return ogEl
            soFound = true
            return {
                ...ogEl,
                elements: [
                    ...soElements.slice(0, soIndex),
                    updateSO(soElements[soIndex]!),
                    ...soElements.slice(soIndex + 1),
                ],
            }
        })
        if (!soFound) return node
        done = true
        return { ...node, elements: newElements }
    })
}

// Patches the changeType of all ADDED UOM elements → MODIFIED within the content
// tree. Called on the no-change save path to unmount inline editors without
// overwriting Text sibling values with stale store state.
export const patchUOMChangeType = (
    nodes: Partial<ContentItemViewNodesType>[],
    selectionOptionId: string
): Partial<ContentItemViewNodesType>[] =>
    updateSOInNodes(nodes, selectionOptionId, (so) => ({
        ...so,
        elements: (so.elements ?? []).map((el) =>
            el.type === ELEMENT_TYPE.UNITS_OF_MEASURE && el.changeType === TrackChangeType.ADDED
                ? { ...el, changeType: TrackChangeType.MODIFIED }
                : el
        ),
    }))
